import { MacroTargets, NutritionCalculationResult } from './types';

export type DietaryPreference = 
  | 'standard' 
  | 'vegetarian' 
  | 'vegan' 
  | 'keto' 
  | 'carnivore'
  | 'mediterranean'
  | 'paleo'
  | 'low-carb'
  | 'high-protein'
  | 'gluten-free'
  | 'halal'
  | 'kosher';

export interface UserBodyStats {
  age: number;
  gender: 'male' | 'female' | 'other';
  height: number; // cm
  weight: number; // kg
  targetWeight?: number; // kg
  goal: 'lose' | 'maintain' | 'gain';
  activityLevel: 'sedentary' | 'light' | 'moderate' | 'active' | 'very_active';
  dietaryPreferences: DietaryPreference[]; // New: dietary preferences
}

/**
 * Evidence-based macronutrient ratios for different dietary patterns
 * Sources: WHO/FAO, American Dietetic Association, Ketogenic Research, Mediterranean Diet Studies
 */
const DIET_MACRO_RATIOS: Record<string, { protein: number; carbs: number; fat: number; notes: string }> = {
  // Standard balanced diet (WHO/FAO recommendations)
  standard: {
    protein: 15, // 10-15% of calories
    carbs: 55,   // 45-65% of calories  
    fat: 30,     // 20-35% of calories
    notes: 'WHO/FAO recommended balanced macronutrient distribution'
  },

  // Ketogenic diet (clinical research-based)
  keto: {
    protein: 20, // 15-25% of calories
    carbs: 5,    // <10% of calories (<50g, ideally <20g)
    fat: 75,     // 70-80% of calories
    notes: 'Clinical ketogenic diet for metabolic ketosis induction'
  },

  // Low-carb (non-ketogenic)
  'low-carb': {
    protein: 25, // 20-30% of calories
    carbs: 20,   // 15-25% of calories (<130g)
    fat: 55,     // 50-60% of calories
    notes: 'Moderate carb restriction without ketosis'
  },

  // Carnivore diet (zero-carb)
  carnivore: {
    protein: 35, // 30-40% of calories
    carbs: 0,    // 0-5% of calories (<20g)
    fat: 65,     // 60-70% of calories
    notes: 'Animal-based, near-zero carbohydrate elimination diet'
  },

  // Mediterranean diet (research-backed)
  mediterranean: {
    protein: 18, // 15-20% of calories
    carbs: 45,   // 40-50% of calories (complex carbs priority)
    fat: 37,     // 35-40% of calories (olive oil, nuts, fish)
    notes: 'Traditional Mediterranean pattern, high MUFA/PUFA ratio'
  },

  // Vegetarian (lacto-ovo)
  vegetarian: {
    protein: 18, // 15-20% of calories (bioavailability consideration)
    carbs: 55,   // 50-60% of calories
    fat: 27,     // 25-30% of calories
    notes: 'Plant-based with dairy/eggs, increased protein for bioavailability'
  },

  // Vegan (plant-based only)
  vegan: {
    protein: 22, // 18-25% of calories (higher due to amino acid completeness)
    carbs: 55,   // 50-60% of calories
    fat: 23,     // 20-25% of calories
    notes: 'Plant-only, elevated protein for amino acid completeness'
  },

  // High-protein (athlete/bodybuilding)
  'high-protein': {
    protein: 35, // 30-40% of calories
    carbs: 35,   // 30-40% of calories
    fat: 30,     // 25-35% of calories
    notes: 'Elevated protein for muscle protein synthesis'
  },

  // Paleo diet
  paleo: {
    protein: 25, // 20-30% of calories
    carbs: 35,   // 30-40% of calories (from fruits/vegetables)
    fat: 40,     // 35-45% of calories
    notes: 'Paleolithic pattern, no grains/legumes/dairy'
  },

  // Gluten-free diet (food restriction, not macro-specific)
  'gluten-free': {
    protein: 15, // 10-15% of calories (standard ratios)
    carbs: 55,   // 45-65% of calories (gluten-free carbs)
    fat: 30,     // 20-35% of calories
    notes: 'Gluten elimination diet, standard macro ratios with wheat alternatives'
  },

  // Halal diet (religious dietary laws, standard nutrition)
  halal: {
    protein: 15, // 10-15% of calories (standard ratios)
    carbs: 55,   // 45-65% of calories
    fat: 30,     // 20-35% of calories
    notes: 'Islamic dietary laws, standard balanced macronutrient distribution'
  },

  // Kosher diet (religious dietary laws, standard nutrition)
  kosher: {
    protein: 15, // 10-15% of calories (standard ratios)
    carbs: 55,   // 45-65% of calories
    fat: 30,     // 20-35% of calories
    notes: 'Jewish dietary laws, standard balanced macronutrient distribution'
  }
};

/**
 * Protein bioavailability and absorption factors for different dietary patterns
 * Sources: WHO/FAO Protein Quality Evaluation, PDCAAS scores
 */
const PROTEIN_BIOAVAILABILITY_FACTORS: Record<string, number> = {
  standard: 1.0,      // Mixed diet baseline
  carnivore: 0.95,    // High bioavailability animal proteins
  keto: 0.95,         // Mostly animal proteins
  'low-carb': 0.92,   // Mixed but animal-heavy
  mediterranean: 0.88, // Mixed with more plant proteins
  vegetarian: 0.85,   // Some animal proteins (dairy/eggs)
  vegan: 0.75,        // Plant-only, lower PDCAAS scores
  'high-protein': 0.90, // Usually mixed sources
  paleo: 0.90,        // Animal + plant combination
  'gluten-free': 0.88, // Depends on protein sources
  halal: 1.0,         // No specific bioavailability difference
  kosher: 1.0         // No specific bioavailability difference
};

/**
 * Special considerations and micronutrient adjustments for dietary patterns
 */
const DIET_SPECIAL_CONSIDERATIONS: Record<string, {
  ironMultiplier: number;
  b12Required: boolean;
  calciumMultiplier: number;
  omega3Required: boolean;
  fiberTarget: number;
  sodiumLimit: number;
}> = {
  vegan: {
    ironMultiplier: 1.8,     // Higher iron needs due to non-heme iron
    b12Required: true,       // B12 supplementation critical
    calciumMultiplier: 1.2,  // Higher calcium needs
    omega3Required: true,    // EPA/DHA from algae
    fiberTarget: 40,         // Higher fiber intake
    sodiumLimit: 2000        // Standard limit
  },
  vegetarian: {
    ironMultiplier: 1.4,     // Moderate iron increase
    b12Required: false,      // Usually adequate from dairy/eggs
    calciumMultiplier: 1.0,  // Normal from dairy
    omega3Required: false,   // Can get from eggs/fish (if pescatarian)
    fiberTarget: 35,         // High fiber
    sodiumLimit: 2000
  },
  keto: {
    ironMultiplier: 1.0,
    b12Required: false,
    calciumMultiplier: 1.0,
    omega3Required: false,
    fiberTarget: 20,         // Lower due to low carb
    sodiumLimit: 3000        // Higher due to electrolyte needs
  },
  carnivore: {
    ironMultiplier: 0.8,     // High bioavailable heme iron
    b12Required: false,      // Abundant in animal products
    calciumMultiplier: 1.2,  // No dairy sources
    omega3Required: false,   // From fatty fish/meat
    fiberTarget: 0,          // Essentially zero fiber
    sodiumLimit: 3500        // Higher electrolyte needs
  },
  mediterranean: {
    ironMultiplier: 1.0,
    b12Required: false,
    calciumMultiplier: 1.0,
    omega3Required: false,   // High fish consumption
    fiberTarget: 35,         // High from fruits/vegetables
    sodiumLimit: 2000
  },
  'gluten-free': {
    ironMultiplier: 1.2,     // May need more iron due to fortified grain avoidance
    b12Required: false,      // Usually adequate from animal products
    calciumMultiplier: 1.1,  // May need more calcium due to fortified grain avoidance
    omega3Required: false,   // Standard sources available
    fiberTarget: 30,         // Standard fiber target
    sodiumLimit: 2300        // Standard limit
  },
  halal: {
    ironMultiplier: 1.0,     // Standard iron needs
    b12Required: false,      // Standard sources available
    calciumMultiplier: 1.0,  // Standard calcium needs
    omega3Required: false,   // Standard sources available
    fiberTarget: 25,         // Standard fiber target
    sodiumLimit: 2300        // Standard limit
  },
  kosher: {
    ironMultiplier: 1.0,     // Standard iron needs
    b12Required: false,      // Standard sources available
    calciumMultiplier: 1.0,  // Standard calcium needs
    omega3Required: false,   // Standard sources available
    fiberTarget: 25,         // Standard fiber target
    sodiumLimit: 2300        // Standard limit
  }
};

/**
 * Calculate comprehensive nutrition targets based on individual body stats and dietary preferences
 * Uses evidence-based formulas and research-backed dietary pattern recommendations
 */
export function calculateNutritionTargets(stats: UserBodyStats): NutritionCalculationResult {
  // 1. Calculate BMR using Mifflin-St Jeor Equation (most accurate)
  let bmr: number;
  if (stats.gender === 'male') {
    bmr = 88.362 + (13.397 * stats.weight) + (4.799 * stats.height) - (5.677 * stats.age);
  } else if (stats.gender === 'female') {
    bmr = 447.593 + (9.247 * stats.weight) + (3.098 * stats.height) - (4.330 * stats.age);
  } else {
    // For 'other' gender, use average of male/female formulas
    const bmrMale = 88.362 + (13.397 * stats.weight) + (4.799 * stats.height) - (5.677 * stats.age);
    const bmrFemale = 447.593 + (9.247 * stats.weight) + (3.098 * stats.height) - (4.330 * stats.age);
    bmr = (bmrMale + bmrFemale) / 2;
  }

  // 2. Apply activity level multiplier (WHO/FAO guidelines)
  const activityMultipliers = {
    sedentary: 1.2,      // Little/no exercise
    light: 1.375,        // Light exercise 1-3 days/week  
    moderate: 1.55,      // Moderate exercise 3-5 days/week
    active: 1.725,       // Hard exercise 6-7 days/week
    very_active: 1.9     // Very hard exercise, physical job
  };

  const tdee = bmr * activityMultipliers[stats.activityLevel];

  // 3. Apply goal adjustment (evidence-based calorie deficits/surpluses)
  let goalAdjustment = 0;
  let dailyCalories = tdee;

  if (stats.goal === 'lose') {
    goalAdjustment = -0.20; // 20% deficit = ~1lb/week loss (safe rate)
    dailyCalories = tdee * 0.8;
  } else if (stats.goal === 'gain') {
    goalAdjustment = 0.15;  // 15% surplus = lean muscle gain
    dailyCalories = tdee * 1.15;
  }
  // maintain: no adjustment needed

  // 4. Determine primary dietary pattern (prioritize most restrictive)
  const primaryDiet = determinePrimaryDiet(stats.dietaryPreferences);
  const dietRatios = DIET_MACRO_RATIOS[primaryDiet];
  const proteinBioavailability = PROTEIN_BIOAVAILABILITY_FACTORS[primaryDiet];
  const specialConsiderations = DIET_SPECIAL_CONSIDERATIONS[primaryDiet];

  // Add error handling for missing diet data
  if (!dietRatios) {
    console.warn(`⚠️ Missing macro ratios for diet: ${primaryDiet}. Falling back to standard diet.`);
    const fallbackDiet = DIET_MACRO_RATIOS['standard'];
    const fallbackBioavailability = PROTEIN_BIOAVAILABILITY_FACTORS['standard'];
    
    // Use fallback values
    const proteinCalories = dailyCalories * (fallbackDiet.protein / 100);
    const carbCalories = dailyCalories * (fallbackDiet.carbs / 100);
    const fatCalories = dailyCalories * (fallbackDiet.fat / 100);
    
    // Convert and adjust protein
    let proteinGrams = (proteinCalories / 4) / fallbackBioavailability;
    const carbGrams = carbCalories / 4;
    const fatGrams = fatCalories / 9;
    
    // Build macro targets with fallback
    const macroTargets: MacroTargets = {
      protein: { min: proteinGrams * 0.9, max: proteinGrams * 1.1, target: proteinGrams },
      carbs: { min: carbGrams * 0.8, max: carbGrams * 1.2, target: carbGrams },
      fat: { min: fatGrams * 0.8, max: fatGrams * 1.2, target: fatGrams },
      fiber: { target: 25 },
      sodium: { max: 2300 },
      potassium: { target: 3500 },
      iron: { target: stats.gender === 'female' && stats.age <= 50 ? 18 : 8 },
      calcium: { target: 1000 },
      vitaminD: { target: 600 },
      saturatedFat: { max: dailyCalories * 0.10 / 9 },
      sugar: { max: dailyCalories * 0.10 / 4 },
      cholesterol: { max: 300 }
    };
    
    return {
      dailyCalories: Math.round(dailyCalories),
      macroTargets,
      calculationMethod: 'mifflin-st-jeor',
      activityMultiplier: activityMultipliers[stats.activityLevel],
      goalAdjustment,
      proteinPerKg: proteinGrams / stats.weight,
      fatPercentage: Math.round((fatCalories / dailyCalories) * 100),
      carbPercentage: Math.round((carbCalories / dailyCalories) * 100),
      proteinPercentage: Math.round((proteinCalories / dailyCalories) * 100),
      dietaryPattern: primaryDiet,
      bioavailabilityFactor: fallbackBioavailability,
      specialConsiderations: undefined
    };
  }

  // 5. Calculate macronutrient targets based on dietary pattern
  const proteinCalories = dailyCalories * (dietRatios.protein / 100);
  const carbCalories = dailyCalories * (dietRatios.carbs / 100);
  const fatCalories = dailyCalories * (dietRatios.fat / 100);

  // Convert calories to grams (protein: 4 cal/g, carbs: 4 cal/g, fat: 9 cal/g)
  let proteinGrams = proteinCalories / 4;
  const carbGrams = carbCalories / 4;
  const fatGrams = fatCalories / 9;

  // 6. Adjust protein for bioavailability (higher intake needed for plant-based diets)
  proteinGrams = proteinGrams / proteinBioavailability;

  // 7. Calculate micronutrient targets with dietary considerations
  const baseIronTarget = stats.gender === 'female' && stats.age <= 50 ? 18 : 8; // mg
  const ironTarget = specialConsiderations ? 
    baseIronTarget * specialConsiderations.ironMultiplier : baseIronTarget;

  const baseFiberTarget = 25; // grams (minimum)
  const fiberTarget = specialConsiderations?.fiberTarget || baseFiberTarget;

  const sodiumLimit = specialConsiderations?.sodiumLimit || 2300; // mg

  // 8. Build comprehensive macro targets
  const macroTargets: MacroTargets = {
    // Macronutrients (ranges for flexibility)
    protein: { 
      min: proteinGrams * 0.9, 
      max: proteinGrams * 1.1, 
      target: proteinGrams 
    },
    carbs: { 
      min: primaryDiet === 'keto' ? 0 : carbGrams * 0.8, 
      max: primaryDiet === 'keto' ? 20 : carbGrams * 1.2, 
      target: carbGrams 
    },
    fat: { 
      min: fatGrams * 0.8, 
      max: fatGrams * 1.2, 
      target: fatGrams 
    },
    fiber: { target: fiberTarget },
    
    // Micronutrients (evidence-based limits/targets)
    sodium: { max: sodiumLimit },
    potassium: { target: 3500 }, // mg (WHO recommendation)
    iron: { target: ironTarget },
    calcium: { target: specialConsiderations ? 1000 * specialConsiderations.calciumMultiplier : 1000 }, // mg
    vitaminD: { target: 600 }, // IU (RDA for most adults)
    saturatedFat: { max: dailyCalories * 0.10 / 9 }, // <10% of calories
    sugar: { max: dailyCalories * 0.10 / 4 }, // <10% of calories (added sugars)
    cholesterol: { max: 300 } // mg
  };

  return {
    dailyCalories: Math.round(dailyCalories),
    macroTargets,
    calculationMethod: 'mifflin-st-jeor',
    activityMultiplier: activityMultipliers[stats.activityLevel],
    goalAdjustment,
    proteinPerKg: proteinGrams / stats.weight,
    fatPercentage: Math.round((fatCalories / dailyCalories) * 100),
    carbPercentage: Math.round((carbCalories / dailyCalories) * 100),
    proteinPercentage: Math.round((proteinCalories / dailyCalories) * 100),
    dietaryPattern: primaryDiet,
    bioavailabilityFactor: proteinBioavailability,
    specialConsiderations: specialConsiderations ? {
      requiresB12Supplementation: specialConsiderations.b12Required,
      requiresOmega3Supplementation: specialConsiderations.omega3Required,
      ironAbsorptionNote: proteinBioavailability < 0.9 ? 
        "Consume vitamin C with iron-rich foods to enhance absorption" : undefined
    } : undefined
  };
}

/**
 * Determine primary dietary pattern from user preferences
 * Priority order: Most restrictive to least restrictive
 */
function determinePrimaryDiet(preferences: DietaryPreference[]): string {
  if (!preferences || preferences.length === 0) return 'standard';
  
  // Priority order (most restrictive first)
  const priorityOrder: DietaryPreference[] = [
    'carnivore',     // Most restrictive
    'keto',          
    'vegan',         
    'vegetarian',    
    'paleo',         
    'low-carb',      
    'high-protein',  
    'mediterranean', 
    'gluten-free',   
    'halal',         
    'kosher'         // Least restrictive
  ];

  // Return first match in priority order
  for (const diet of priorityOrder) {
    if (preferences.includes(diet)) {
      return diet;
    }
  }

  return 'standard';
}

/**
 * Get dietary pattern information for user education
 */
export function getDietaryPatternInfo(preferences: DietaryPreference[]) {
  const primaryDiet = determinePrimaryDiet(preferences);
  const ratios = DIET_MACRO_RATIOS[primaryDiet];
  const considerations = DIET_SPECIAL_CONSIDERATIONS[primaryDiet];
  
  return {
    primaryPattern: primaryDiet,
    macroRatios: ratios,
    specialNeeds: considerations,
    description: ratios.notes,
    proteinBioavailability: PROTEIN_BIOAVAILABILITY_FACTORS[primaryDiet]
  };
}

/**
 * Validate if daily nutrition intake aligns with calculated targets
 */
export function validateNutritionAlignment(
  dailyIntake: { calories: number; protein: number; carbs: number; fat: number },
  targets: MacroTargets
): {
  isWithinTargets: boolean;
  feedback: string[];
  alignmentScore: number; // 0-100
} {
  const feedback: string[] = [];
  let score = 100;

  // Check protein range
  if (dailyIntake.protein < targets.protein.min) {
    feedback.push(`Protein too low (${dailyIntake.protein.toFixed(1)}g vs ${targets.protein.target.toFixed(1)}g target)`);
    score -= 15;
  } else if (dailyIntake.protein > targets.protein.max) {
    feedback.push(`Protein too high (${dailyIntake.protein.toFixed(1)}g vs ${targets.protein.target.toFixed(1)}g target)`);
    score -= 10;
  }

  // Check carb range  
  if (dailyIntake.carbs < targets.carbs.min) {
    feedback.push(`Carbs too low (${dailyIntake.carbs.toFixed(1)}g vs ${targets.carbs.target.toFixed(1)}g target)`);
    score -= 15;
  } else if (dailyIntake.carbs > targets.carbs.max) {
    feedback.push(`Carbs too high (${dailyIntake.carbs.toFixed(1)}g vs ${targets.carbs.target.toFixed(1)}g target)`);
    score -= 15;
  }

  // Check fat range
  if (dailyIntake.fat < targets.fat.min) {
    feedback.push(`Fat too low (${dailyIntake.fat.toFixed(1)}g vs ${targets.fat.target.toFixed(1)}g target)`);
    score -= 15;
  } else if (dailyIntake.fat > targets.fat.max) {
    feedback.push(`Fat too high (${dailyIntake.fat.toFixed(1)}g vs ${targets.fat.target.toFixed(1)}g target)`);
    score -= 10;
  }

  return {
    isWithinTargets: feedback.length === 0,
    feedback,
    alignmentScore: Math.max(0, score)
  };
}

/**
 * Calculate macro percentages for display
 */
export function calculateMacroPercentages(
  nutrition: { protein: number; carbs: number; fat: number },
  targets: MacroTargets
) {
  return {
    protein: {
      percentage: Math.min((nutrition.protein / targets.protein.target) * 100, 100),
      status: nutrition.protein >= targets.protein.min && nutrition.protein <= targets.protein.max ? 'good' :
              nutrition.protein < targets.protein.min ? 'low' : 'high'
    },
    carbs: {
      percentage: Math.min((nutrition.carbs / targets.carbs.target) * 100, 100),
      status: nutrition.carbs >= targets.carbs.min && nutrition.carbs <= targets.carbs.max ? 'good' :
              nutrition.carbs < targets.carbs.min ? 'low' : 'high'
    },
    fat: {
      percentage: Math.min((nutrition.fat / targets.fat.target) * 100, 100),
      status: nutrition.fat >= targets.fat.min && nutrition.fat <= targets.fat.max ? 'good' :
              nutrition.fat < targets.fat.min ? 'low' : 'high'
    }
  };
}

/**
 * Get nutrition status colors for UI
 */
export function getNutritionStatusColor(status: 'good' | 'low' | 'high') {
  switch (status) {
    case 'good':
      return { bg: 'bg-green-50 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', bar: 'bg-green-500' };
    case 'low':
      return { bg: 'bg-amber-50 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', bar: 'bg-amber-500' };
    case 'high':
      return { bg: 'bg-red-50 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', bar: 'bg-red-500' };
    default:
      return { bg: 'bg-gray-50 dark:bg-gray-900/30', text: 'text-gray-700 dark:text-gray-300', bar: 'bg-gray-500' };
  }
} 